import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { AlertTriangle, CheckCircle2, Code2, FileSearch, Link2, RefreshCw, Bot, Send } from "lucide-react";
import { amIAdmin } from "@/lib/admin.functions";
import { useSession } from "@/lib/user-data";
import {
  canonicalSmokeTest,
  checkRobotsTxt,
  previewCanonical,
  validateStructuredData,
} from "@/lib/seo-tools.functions";
import { inspectUrl, listSeoProperties, refreshSitemaps } from "@/lib/search-console.functions";
import { breadcrumbSchema, webPageSchema } from "@/lib/schema";

export const Route = createFileRoute("/seo-audit")({
  head: () => ({
    meta: [
      { title: "SEO audit tools — Pal Stars" },
      { name: "description", content: "Internal Pal Stars SEO toolbox: canonical preview, structured-data validator, robots.txt generator, sitemap refresh and URL inspection." },
      { property: "og:title", content: "SEO audit tools — Pal Stars" },
      { property: "og:description", content: "Internal Pal Stars SEO toolbox for canonical, schema, robots and sitemap checks." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/seo-audit" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/seo-audit" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["SEO audit tools", "/seo-audit"]]),
      webPageSchema({
        name: "SEO audit tools",
        description: "Internal Pal Stars SEO toolbox: canonical preview, structured-data validation, robots.txt generation, sitemap refresh and URL inspection.",
        path: "/seo-audit",
      }),
    ],
  }),
  component: SeoAuditPage,
});

function Card({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="glass rounded-xl p-5">
      <h2 className="flex items-center gap-2 text-lg font-semibold">{icon} {title}</h2>
      <div className="mt-4 space-y-3 text-sm">{children}</div>
    </section>
  );
}

function Issue({ text, kind }: { text: string; kind: "error" | "warn" }) {
  return (
    <li className={`flex gap-2 ${kind === "error" ? "text-destructive" : "text-muted-foreground"}`}>
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> <span>{text}</span>
    </li>
  );
}

/** Tool 1 — canonical + SERP snippet preview for any path. */
function CanonicalPreview() {
  const run = useServerFn(previewCanonical);
  const [path, setPath] = useState("/booster-pack");
  const m = useMutation({ mutationFn: (p: string) => run({ data: { path: p } }) });
  const d = m.data;

  return (
    <Card title="Canonical & snippet preview" icon={<Link2 className="h-4 w-4 text-primary" />}>
      <form
        className="flex gap-2"
        onSubmit={(e) => { e.preventDefault(); m.mutate(path); }}
      >
        <label htmlFor="canonical-path" className="sr-only">Path to preview</label>
        <input
          id="canonical-path"
          value={path}
          onChange={(e) => setPath(e.target.value)}
          aria-label="Path to preview, for example /booster-pack"
          className="flex-1 rounded-md border bg-background/60 px-3 py-2 text-sm outline-none focus:ring-2 ring-primary"
        />
        <button className="rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground" aria-label="Preview this page's search snippet">
          {m.isPending ? "Checking…" : "Preview"}
        </button>
      </form>

      {m.error && <p className="text-destructive">{(m.error as Error).message}</p>}

      {d && (
        <>
          <div className="rounded-lg border bg-background/50 p-4">
            <p className="text-xs text-muted-foreground">{d.serp.url}</p>
            <p className="mt-1 text-base text-[#8ab4f8]">{d.serp.title || "(no title)"}</p>
            <p className="mt-1 text-xs text-muted-foreground">{d.serp.description || "(no description)"}</p>
          </div>
          <dl className="grid gap-1 text-xs sm:grid-cols-2">
            <div><dt className="text-muted-foreground">canonical</dt><dd className="break-all">{d.head.canonical || "—"}</dd></div>
            <div><dt className="text-muted-foreground">og:url</dt><dd className="break-all">{d.head.ogUrl || "—"}</dd></div>
            <div><dt className="text-muted-foreground">title length</dt><dd>{d.head.title.length}</dd></div>
            <div><dt className="text-muted-foreground">description length</dt><dd>{d.head.description.length}</dd></div>
            <div><dt className="text-muted-foreground">H1 count</dt><dd>{d.head.h1s.length}</dd></div>
            <div><dt className="text-muted-foreground">og:image</dt><dd className="break-all">{d.head.ogImage || "—"}</dd></div>
          </dl>
          {d.issues.length === 0 && d.warnings.length === 0 ? (
            <p className="flex items-center gap-2 text-primary"><CheckCircle2 className="h-4 w-4" /> Canonical, snippet and social tags all clean.</p>
          ) : (
            <ul className="space-y-1 text-xs">
              {d.issues.map((i) => <Issue key={i} text={i} kind="error" />)}
              {d.warnings.map((i) => <Issue key={i} text={i} kind="warn" />)}
            </ul>
          )}
        </>
      )}
    </Card>
  );
}

/** Tool 2 — canonical smoke test over every tracked route. */
function CanonicalSmoke() {
  const run = useServerFn(canonicalSmokeTest);
  const { data, isFetching, refetch } = useQuery({ queryKey: ["canonical-smoke"], queryFn: () => run({}), enabled: false });
  const failing = (data?.results ?? []).filter((r) => r.issues.length > 0);

  return (
    <Card title="Canonical smoke test" icon={<FileSearch className="h-4 w-4 text-primary" />}>
      <button
        onClick={() => refetch()}
        className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold hover:bg-card"
        aria-label="Run the canonical smoke test across every tracked route"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} /> {isFetching ? "Crawling…" : "Run smoke test"}
      </button>
      {data && (
        failing.length === 0 ? (
          <p className="flex items-center gap-2 text-primary"><CheckCircle2 className="h-4 w-4" /> All {data.results.length} routes self-canonicalise with one H1 and no duplicate social tags.</p>
        ) : (
          <ul className="space-y-1 text-xs">
            {failing.map((r) => <Issue key={r.path} text={`${r.path}: ${r.issues.join("; ")}`} kind="error" />)}
          </ul>
        )
      )}
    </Card>
  );
}

/** Tool 3 — structured-data validator. */
function SchemaValidator() {
  const run = useServerFn(validateStructuredData);
  const [path, setPath] = useState("/");
  const m = useMutation({ mutationFn: (p: string) => run({ data: { path: p } }) });
  const d = m.data;

  return (
    <Card title="Structured data validator" icon={<Code2 className="h-4 w-4 text-primary" />}>
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); m.mutate(path); }}>
        <label htmlFor="schema-path" className="sr-only">Path to validate</label>
        <input
          id="schema-path"
          value={path}
          onChange={(e) => setPath(e.target.value)}
          aria-label="Path to validate structured data for"
          className="flex-1 rounded-md border bg-background/60 px-3 py-2 text-sm outline-none focus:ring-2 ring-primary"
        />
        <button className="rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground" aria-label="Validate this page's structured data">
          {m.isPending ? "Validating…" : "Validate"}
        </button>
      </form>
      {m.error && <p className="text-destructive">{(m.error as Error).message}</p>}
      {d && (
        <>
          <p className="text-xs text-muted-foreground">
            {d.blocks.length} JSON-LD block(s) · rich-result types: {d.richResultTypes.join(", ") || "none"}
          </p>
          <ul className="space-y-1 text-xs">
            {d.blocks.map((b, i) => (
              <li key={`${b.type}-${i}`} className={b.valid ? "text-primary" : "text-destructive"}>
                {b.valid ? "✓" : "✗"} {b.type}
                {b.errors.length > 0 && <span> — {b.errors.join("; ")}</span>}
                {b.warnings.length > 0 && <span className="text-muted-foreground"> ({b.warnings.join("; ")})</span>}
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}

/** Tool 4 — robots.txt validation + generator. */
function RobotsTool() {
  const run = useServerFn(checkRobotsTxt);
  const { data, isFetching, refetch } = useQuery({ queryKey: ["robots-check"], queryFn: () => run({}), staleTime: 60_000 });

  return (
    <Card title="robots.txt validator & generator" icon={<Bot className="h-4 w-4 text-primary" />}>
      <button
        onClick={() => refetch()}
        className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold hover:bg-card"
        aria-label="Re-check the live robots.txt file"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} /> Re-check
      </button>
      {data && (
        <>
          {data.ok
            ? <p className="flex items-center gap-2 text-primary"><CheckCircle2 className="h-4 w-4" /> Live robots.txt is valid: {data.disallow.length} Disallow rules, {data.sitemaps.length} sitemap(s).</p>
            : <ul className="space-y-1 text-xs">{data.issues.map((i) => <Issue key={i} text={i} kind="error" />)}</ul>}
          {!data.matchesSuggested && (
            <>
              <p className="text-xs text-muted-foreground">Generated robots.txt (differs from live — paste into public/robots.txt):</p>
              <pre className="max-h-64 overflow-auto rounded-md border bg-background/60 p-3 text-[11px]">{data.suggested}</pre>
            </>
          )}
        </>
      )}
    </Card>
  );
}

/** Tool 5 — sitemap refresh + URL inspection through Search Console. */
function SearchConsoleTools() {
  const props = useServerFn(listSeoProperties);
  const refresh = useServerFn(refreshSitemaps);
  const inspect = useServerFn(inspectUrl);
  const { data: properties } = useQuery({ queryKey: ["seo-properties"], queryFn: () => props({}) });
  const site = properties?.properties?.[0];
  const [url, setUrl] = useState("https://palstars.com/booster-pack");

  const refreshM = useMutation({ mutationFn: () => refresh({ data: { siteUrl: site! } }) });
  const inspectM = useMutation({ mutationFn: (u: string) => inspect({ data: { siteUrl: site!, url: u } }) });

  return (
    <Card title="Sitemap refresh & URL inspection" icon={<Send className="h-4 w-4 text-primary" />}>
      <p className="text-xs text-muted-foreground">Property: {site ?? "resolving…"}</p>
      <button
        disabled={!site || refreshM.isPending}
        onClick={() => refreshM.mutate()}
        className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-40"
        aria-label="Re-submit both sitemaps to Google Search Console"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${refreshM.isPending ? "animate-spin" : ""}`} /> Refresh sitemaps in Search Console
      </button>
      {refreshM.data && (
        <ul className="space-y-1 text-xs">
          {refreshM.data.results.map((r) => (
            <li key={r.sitemap} className={r.submitted ? "text-primary" : "text-destructive"}>
              {r.submitted ? "✓ submitted" : "✗ failed"} {r.sitemap}
              {r.errors ? ` — ${r.errors} error(s)` : ""}{r.warnings ? `, ${r.warnings} warning(s)` : ""}
              {r.error ? ` — ${r.error}` : ""}
            </li>
          ))}
        </ul>
      )}
      {refreshM.error && <p className="text-destructive text-xs">{(refreshM.error as Error).message}</p>}

      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (site) inspectM.mutate(url); }}>
        <label htmlFor="inspect-url" className="sr-only">URL to inspect</label>
        <input
          id="inspect-url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          aria-label="Full URL to inspect in Google's index"
          className="flex-1 rounded-md border bg-background/60 px-3 py-2 text-sm outline-none focus:ring-2 ring-primary"
        />
        <button disabled={!site} className="rounded-md border px-3 py-2 text-xs font-semibold disabled:opacity-40" aria-label="Read Google's index state for this URL">
          {inspectM.isPending ? "Inspecting…" : "Inspect"}
        </button>
      </form>
      {inspectM.data && (
        <dl className="grid gap-1 text-xs sm:grid-cols-2">
          <div><dt className="text-muted-foreground">verdict</dt><dd>{inspectM.data.index.verdict ?? "—"}</dd></div>
          <div><dt className="text-muted-foreground">coverage</dt><dd>{inspectM.data.index.coverageState ?? "—"}</dd></div>
          <div><dt className="text-muted-foreground">Google canonical</dt><dd className="break-all">{inspectM.data.index.googleCanonical ?? "—"}</dd></div>
          <div><dt className="text-muted-foreground">declared canonical</dt><dd className="break-all">{inspectM.data.index.userCanonical ?? "—"}</dd></div>
          <div><dt className="text-muted-foreground">last crawl</dt><dd>{inspectM.data.index.lastCrawlTime ?? "—"}</dd></div>
          <div><dt className="text-muted-foreground">rich results</dt><dd>{inspectM.data.richResults.verdict} {inspectM.data.richResults.types.join(", ")}</dd></div>
          <div className="sm:col-span-2"><dd className="text-muted-foreground">{inspectM.data.note}</dd></div>
        </dl>
      )}
      {inspectM.error && <p className="text-destructive text-xs">{(inspectM.error as Error).message}</p>}
    </Card>
  );
}

function SeoAuditPage() {
  const { session, loading } = useSession();
  const admin = useServerFn(amIAdmin);
  const { data } = useQuery({ queryKey: ["am-i-admin"], queryFn: () => admin({}), enabled: Boolean(session) });

  if (loading) return <div className="mx-auto max-w-5xl px-4 py-10 text-sm text-muted-foreground">Loading…</div>;
  if (!session || data?.admin !== true) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10">
        <h1 className="mt-4 text-2xl font-bold">SEO audit tools</h1>
        <p className="mt-2 text-sm text-muted-foreground">These tools are available to Pal Stars admins only.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8 space-y-6">
      <header>
        <h1 className="text-2xl sm:text-3xl font-bold">SEO audit tools</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Canonical and snippet preview, structured-data validation, robots.txt generation, sitemap refresh and Google
          index inspection — all run against the live canonical site.
        </p>
      </header>
      <CanonicalPreview />
      <CanonicalSmoke />
      <SchemaValidator />
      <RobotsTool />
      <SearchConsoleTools />
    </div>
  );
}
